import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useShow } from "@/contexts/ShowContext"
import AssetsEditor from "@/components/AssetsEditor"
import { DEFAULT_ASSET_SORT } from "@/components/AssetsEditor/constants"

const STORAGE_PREFIX = 'assetsEditor.settings'

const STATUS_KEYS = ['draft', 'approved', 'rejected']

const loadSettings = (showId) => {
    try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}.${showId}`)
        if (!raw) return {}
        return JSON.parse(raw) || {}
    } catch {
        return {}
    }
}

const saveSettings = (showId, patch) => {
    try {
        const key = `${STORAGE_PREFIX}.${showId}`
        const raw = localStorage.getItem(key)
        const current = raw ? (JSON.parse(raw) || {}) : {}
        localStorage.setItem(key, JSON.stringify({ ...current, ...patch }))
    } catch {
        // ignore quota or serialization errors
    }
}

// Parse a comma-separated query param into a string array. Returns null when
// the param is absent so callers can fall back to a default.
const parseList = (searchParams, key) => {
    if (!searchParams.has(key)) return null
    return (searchParams.get(key) || '').split(',').map(s => s.trim()).filter(Boolean)
}

// Decode the ?status= param (comma list of enabled statuses) into the
// { draft, approved, rejected } shape the editor expects.
const parseStatus = (searchParams) => {
    const list = parseList(searchParams, 'status')
    if (!list) return null
    return STATUS_KEYS.reduce((acc, key) => ({ ...acc, [key]: list.includes(key) }), {})
}

export default function ShowAssetsPage() {
    const { show } = useShow()
    const [searchParams, setSearchParams] = useSearchParams()

    // Hydrate settings once on mount. URL params take precedence over saved
    // settings so deep links / refresh / back-forward keep working. For the
    // folder specifically we distinguish:
    //   - param missing  → fall back to saved folder
    //   - `?folder=`     → explicit root (overrides saved)
    //   - `?folder=<id>` → that folder
    const [initial] = useState(() => {
        const saved = loadSettings(show._id)
        const urlFolder = searchParams.get('folder')
        const urlPage = parseInt(searchParams.get('page'), 10)
        const urlTags = parseList(searchParams, 'tags')
        const urlStatus = parseStatus(searchParams)
        const urlMedia = parseList(searchParams, 'media')
        return {
            folder: urlFolder !== null ? (urlFolder || null) : (saved.folder ?? null),
            categories: urlTags ?? (Array.isArray(saved.categories) ? saved.categories : undefined),
            status: urlStatus ?? saved.status,
            media: urlMedia ?? undefined,
            sort: searchParams.get('sort') || saved.sort || DEFAULT_ASSET_SORT,
            page: Number.isFinite(urlPage) && urlPage > 0 ? urlPage : (typeof saved.page === 'number' ? saved.page : 1),
            zoom: typeof saved.zoom === 'number' ? saved.zoom : undefined,
        }
    })

    // If the URL had no `folder` param at all but we restored one from storage,
    // mirror it onto the URL so refresh and back/forward keep working. We check
    // for the param being *missing* (=== null) rather than empty, so deep
    // links like `?folder=` keep meaning "explicit root".
    useEffect(() => {
        const urlFolder = searchParams.get('folder')
        if (urlFolder === null && initial.folder) {
            const next = new URLSearchParams(searchParams)
            next.set('folder', initial.folder)
            setSearchParams(next, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Merge a mutation into the current URL search params. Uses the functional
    // updater so concurrent param updates (e.g. folder + page during "Go to
    // folder") don't clobber each other.
    const updateParams = (mutate, opts) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            mutate(next)
            return next
        }, opts)
    }

    const handleFolderChange = (folderId) => {
        // Folder navigation is the meaningful history boundary (replace: false)
        // and it resets paging, so drop a stale ?page= here. Keep the folder
        // param around (empty when at root) so the URL is always an explicit
        // pointer to *where* the user is, overriding the saved folder on refresh.
        updateParams(next => {
            next.set('folder', folderId || '')
            next.delete('page')
        }, { replace: false })
        saveSettings(show._id, { folder: folderId ?? null })
    }

    const handlePageChange = (page) => {
        updateParams(next => {
            if (page > 1) next.set('page', String(page))
            else next.delete('page')
        }, { replace: true })
        saveSettings(show._id, { page })
    }

    const handleCategoriesChange = (categories) => {
        updateParams(next => {
            const all = show.asset_categories.length
            if (Array.isArray(categories) && categories.length < all) {
                next.set('tags', categories.join(','))
            } else {
                next.delete('tags')
            }
        }, { replace: true })
        saveSettings(show._id, { categories })
    }

    const handleStatusChange = (status) => {
        updateParams(next => {
            const enabled = STATUS_KEYS.filter(key => status?.[key])
            if (enabled.length < STATUS_KEYS.length) {
                next.set('status', enabled.join(','))
            } else {
                next.delete('status')
            }
        }, { replace: true })
        saveSettings(show._id, { status })
    }

    const handleMediaChange = (media, allSelected) => {
        updateParams(next => {
            if (allSelected) next.delete('media')
            else next.set('media', (media || []).join(','))
        }, { replace: true })
    }

    const handleSortChange = (sort) => {
        updateParams(next => {
            if (sort === DEFAULT_ASSET_SORT) next.delete('sort')
            else next.set('sort', sort)
        }, { replace: true })
        saveSettings(show._id, { sort })
    }

    // `folder` prop semantics in AssetsEditor: null = root. Honor the URL
    // when present (`?folder=` → null root), fall back to saved.
    const urlFolderParam = searchParams.get('folder')
    const folderForEditor = urlFolderParam !== null
        ? (urlFolderParam || null)
        : (initial.folder || null)

    return (
        <AssetsEditor
            folder={folderForEditor}
            categories={initial.categories}
            status={initial.status}
            media={initial.media}
            sort={initial.sort}
            page={initial.page}
            zoom={initial.zoom}
            onFolderChange={handleFolderChange}
            onCategoriesChange={handleCategoriesChange}
            onStatusChange={handleStatusChange}
            onMediaChange={handleMediaChange}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            onZoomChange={(zoom) => saveSettings(show._id, { zoom })}
        />
    )
}
